import React, { useCallback, useReducer, useContext, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IUpdateRestaurantPropertiesAsPatchForModifier } from 'types';
import { SaveAsPatchActionTypes } from './saveAsPatchModalActions';
import { saveAsPatchReducer } from './saveAsPatchModalReducer';
import { saveAsPatchInitialState, SaveAsPatchContext, ISaveAsPatchState } from './saveAsPatchModalContext';
import {
	IEditModifierRestaurantPropertiesContext,
	EditModifierRestaurantPropertiesContext,
} from '../../../../editModifierRestaurantPropertiesState/editModifierRestaurantPropertiesContext';
import { convertUndefinedFields } from 'utils/helpers';
import { updateModifierRestaurantPropertiesAsPatch } from 'backend/modifiers';

export const SaveAsPatchState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(saveAsPatchReducer, saveAsPatchInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		refetchRestPropsData,
		changeTypeOfModalWindow: changeTypeOfParentModalWindow,
		modifierId,
		customerName,
		fetchedRestaurants,
		IDSelectedRestaurant,
		price,
		supplementPrice,
		quantityMaxLimit,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		inStop,
		isRestricted,
		isPreselected,
		forceDisable,
		isCreatedFromPos,
		hasPortions,
	} = useContext<IEditModifierRestaurantPropertiesContext>(EditModifierRestaurantPropertiesContext);

	useEffect(() => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_INITIAL_FORM_DATA,
			payload: {
				initialFormData: {
					modifierId,
					customerName,
					fetchedRestaurants,
					IDSelectedRestaurantOnRestPropsPage: IDSelectedRestaurant,
					price,
					supplementPrice,
					quantityMaxLimit,
					hasSchedulePriority,
					activationSchedule,
					deactivationSchedule,
					inStop,
					isRestricted,
					isPreselected,
					forceDisable,
					isCreatedFromPos,
					hasPortions,
				},
			},
		});
	}, []);

	const saveData = useCallback(
		async (value: 'saveAndMakeOtherChanges' | 'saveAndClose') => {
			try {
				const dataForSubmit: IUpdateRestaurantPropertiesAsPatchForModifier = {
					modifierId: modifierId,
					restaurantIds:
						state.typeOfChanges === 'basedChanges'
							? [
									state.initialFormData.IDSelectedRestaurantOnRestPropsPage || '',
									...state.IDsSelectedRestaurant,
							  ]
							: state.IDsSelectedRestaurant,
					update: {
						price: {
							shouldUpdate: state.selectedProperties.includes('price'),
							value: state.typeOfChanges === 'basedChanges' ? state.initialFormData.price : state.price,
						},
						supplementPrice: {
							shouldUpdate: state.selectedProperties.includes('supplementPrice'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.supplementPrice
									: state.supplementPrice,
						},
						quantityMaxLimit: {
							shouldUpdate: state.selectedProperties.includes('quantityMaxLimit'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.quantityMaxLimit
									: state.quantityMaxLimit,
						},
						activationCron: {
							shouldUpdate: state.selectedProperties.includes('activation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.activationSchedule
									: state.activationSchedule,
						},
						deactivationCron: {
							shouldUpdate: state.selectedProperties.includes('deactivation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.deactivationSchedule
									: state.deactivationSchedule,
						},
						isDisabled: {
							shouldUpdate: state.selectedProperties.includes('isDisabled'),
							value: state.typeOfChanges === 'basedChanges' ? state.initialFormData.inStop : state.inStop,
						},
						isRestricted: {
							shouldUpdate: state.selectedProperties.includes('isRestricted'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.isRestricted
									: state.isRestricted,
						},
						isPreselected: {
							shouldUpdate: state.selectedProperties.includes('isPreselected'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.isPreselected
									: state.isPreselected,
						},
						forceDisable: {
							shouldUpdate: state.selectedProperties.includes('forceDisable'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.forceDisable
									: state.forceDisable,
						},
					},
				};
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateModifierRestaurantPropertiesAsPatch(convertedData);
				if (value === 'saveAndClose') {
					changeTypeOfParentModalWindow(null);
					refetchRestPropsData(true);
				}
				if (value === 'saveAndMakeOtherChanges') {
					dispatch({ type: SaveAsPatchActionTypes.RESET_FIELDS });
				}
			} catch (err) {
				dispatch({
					type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			} finally {
				hideLoader();
			}
		},
		[showLoader, hideLoader, state],
	);

	const setTypeOfChanges = useCallback(
		(value: ISaveAsPatchState['typeOfChanges']) => {
			dispatch({
				type: SaveAsPatchActionTypes.SET_TYPE_OF_CHANGES,
				payload: {
					typeOfChanges: value,
					IDsSelectedRestaurant:
						value === 'basedChanges'
							? state.IDsSelectedRestaurant.filter(
									(id) => id !== state.initialFormData.IDSelectedRestaurantOnRestPropsPage,
							  )
							: state.IDsSelectedRestaurant,
				},
			});
		},
		[state.IDsSelectedRestaurant, state.initialFormData.IDSelectedRestaurantOnRestPropsPage],
	);

	const setIDsRestaurant = useCallback((value: ISaveAsPatchState['IDsSelectedRestaurant']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setProperties = useCallback((value: ISaveAsPatchState['selectedProperties']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_PROPERTIES,
			payload: {
				selectedProperties: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: ISaveAsPatchState['price']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const changeSupplementPrice = useCallback((value: ISaveAsPatchState['supplementPrice']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_SUPPLEMENT_PRICE,
			payload: {
				supplementPrice: value,
			},
		});
	}, []);

	const changeQuantityMaxLimit = useCallback((value: ISaveAsPatchState['quantityMaxLimit']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_QUANTITY_MAX_LIMIT,
			payload: {
				quantityMaxLimit: value,
			},
		});
	}, []);

	const changeActivationSchedule = useCallback((value: ISaveAsPatchState['activationSchedule']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: ISaveAsPatchState['deactivationSchedule']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const toggleInStop = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IN_STOP,
		});
	}, []);

	const toggleIsRestricted = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IS_RESTRICTED,
		});
	}, []);

	const toggleIsPreselected = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IS_PRESELECTED,
		});
	}, []);

	const toggleForceDisable = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_FORCE_DISABLE,
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: ISaveAsPatchState['typeOfModalWindow']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<SaveAsPatchContext.Provider
			value={{
				saveData,
				setTypeOfChanges,
				setIDsRestaurant,
				setProperties,
				changePrice,
				changeSupplementPrice,
				changeQuantityMaxLimit,
				changeActivationSchedule,
				changeDeactivationSchedule,
				toggleInStop,
				toggleIsRestricted,
				toggleIsPreselected,
				toggleForceDisable,
				changeTypeOfModalWindow,
				initialFormData: state.initialFormData,
				typeOfChanges: state.typeOfChanges,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedProperties: state.selectedProperties,
				price: state.price,
				supplementPrice: state.supplementPrice,
				quantityMaxLimit: state.quantityMaxLimit,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				inStop: state.inStop,
				isRestricted: state.isRestricted,
				isPreselected: state.isPreselected,
				forceDisable: state.forceDisable,
				isCreatedFromPos: state.isCreatedFromPos,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</SaveAsPatchContext.Provider>
	);
};
